import { NavLink } from 'react-router-dom';
import { Cpu, LayoutDashboard, Send, ShieldCheck, SlidersHorizontal, Sparkles, Users, type LucideIcon } from 'lucide-react';
import type { UserRole } from '@command620/shared';
import { useAuth } from '@/lib/auth-context';
import { cn } from '@/lib/utils';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Hide for roles below this (server still enforces - PLATFORM §8, APP-FLOW §0). */
  minRole?: UserRole;
  end?: boolean;
}

/** Single source of truth for sidebar + command palette (APP-FLOW §0 nav map). */
export const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/prospects', label: 'Prospects', icon: Users },
  { to: '/enrichment', label: 'Enrichment', icon: Cpu },
  { to: '/campaigns', label: 'Campaigns', icon: Send },
  { to: '/deliverability', label: 'Deliverability', icon: ShieldCheck, minRole: 'manager' },
  { to: '/director', label: 'Marketing Director', icon: Sparkles, minRole: 'rep' },
  { to: '/settings', label: 'Settings / Team', icon: SlidersHorizontal },
];

export function useVisibleNav(): NavItem[] {
  const { can } = useAuth();
  return NAV_ITEMS.filter((i) => !i.minRole || can(i.minRole));
}

export function NavList({ expanded, onNavigate }: { expanded: boolean; onNavigate?: () => void }) {
  const items = useVisibleNav();
  return (
    <nav aria-label="Main" className="flex flex-1 flex-col gap-0.5 overflow-auto p-2">
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          title={expanded ? undefined : label}
          className={({ isActive }) =>
            cn(
              'flex h-8 items-center gap-2 rounded-md border-l-2 px-2 text-sm font-medium transition-colors pointer-coarse:h-11',
              !expanded && 'justify-center px-0',
              isActive
                ? 'border-accent bg-active text-fg-accent'
                : 'border-transparent text-fg-muted hover:bg-hover hover:text-fg',
            )
          }
        >
          <Icon className="size-[18px] shrink-0" strokeWidth={1.75} aria-hidden />
          <span className={cn(!expanded && 'sr-only')}>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}
